import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { useState, useEffect, useCallback, useRef } from 'react';
import { driveService } from './services/driveService';
import { chatService } from './services/chatService';
import { documentService } from './services/documentService';
import { memoryService } from './services/memoryService';
import { ChatSidebar } from "./components/layout/ChatSidebar";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "./components/ui/resizable";
import { ChatMessages } from "./components/chat/ChatMessages";
import { SideTabPanel } from "./components/layout/SideTabPanel";
import { ChatInput } from "./components/chat/ChatInput";
import { SettingsPage } from "./components/pages/SettingsPage";
import { DocumentsPage } from "./components/pages/DocumentsPage";
import { ToolsPage } from "./components/pages/ToolsPage";
import { AccountPage } from "./components/pages/AccountPage";
import { MCPSettings } from "./components/settings/MCPSettings";
import AuthModal from './components/layout/AuthModal';
import { DocumentCanvas } from './components/DocumentCanvas';
import { Message, AgentStep, FileAttachment, User, ConnectedAccount, AuthState } from './types';
import { 
  Settings, Shield, FileText, Wrench, User as UserIcon,
  Plus, MessageSquare, Search, Menu, X,
  RefreshCw, Sparkles, Users2
} from 'lucide-react';

const queryClient = new QueryClient();

const BACKEND_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

export default function App() {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    connectedAccounts: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [currentView, setCurrentView] = useState<'chat' | 'documents' | 'tools' | 'account'>('chat');
  
  // Chat state
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [isStreamingResponse, setIsStreamingResponse] = useState(false);
  const [streamingSteps, setStreamingSteps] = useState<AgentStep[]>([]);
  const [streamingContent, setStreamingContent] = useState("");
  const [memorySteps, setMemorySteps] = useState<Array<{ type: 'memory_query' | 'memory_store'; status: 'in_progress' | 'completed' | 'no_results'; message: string }>>([]);
  const [showMemorySteps, setShowMemorySteps] = useState(false);
  const [expandedReasoning, setExpandedReasoning] = useState<Set<string>>(new Set());
  const [documentPreviews, setDocumentPreviews] = useState<Record<string, any[]>>({});
  const [respondedEmailIds, setRespondedEmailIds] = useState<Set<string>>(new Set());
  const [isComposingEmail, setIsComposingEmail] = useState(false);
  
  useEffect(() => {
    (window as any).__respondedEmailIds = respondedEmailIds;
  }, [respondedEmailIds]);
  
  // Document canvas state
  const [documentCanvas, setDocumentCanvas] = useState<{
    isOpen: boolean;
    content: string;
    title: string;
    metadata?: any;
  }>({
    isOpen: false,
    content: '',
    title: '',
    metadata: undefined
  });
  const [showMcpSettings, setShowMcpSettings] = useState(false);
  const [sidebarPanelSize, setSidebarPanelSize] = useState<number>(() => {
    const v = localStorage.getItem('sidebar-panel-size');
    return v ? Number(v) : 30; // percentage of container width
  });
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);
  useEffect(() => {
    localStorage.setItem('sidebar-panel-size', String(sidebarPanelSize));
  }, [sidebarPanelSize]);

  // Context panel state
  const [contextContent, setContextContent] = useState<{
    type: 'emails' | 'documents' | 'web_search' | 'email_composer' | 'email_reply' | 'none';
    data?: any;
    title?: string;
  }>({ type: 'none' });

  // Auto-open/close sidebar based on context
  useEffect(() => {
    const hasContext = contextContent.type !== 'none';
    setSidebarOpen(hasContext);
    if (hasContext) {
      // Ensure sidebar is visible with at least 20% width if it was fully collapsed
      setSidebarPanelSize(prev => (prev <= 1 ? (Number(localStorage.getItem('sidebar-panel-size')) || 30) : prev));
    }
  }, [contextContent.type]);

  // Listen for document preview events from DocumentsPage
  useEffect(() => {
    const onOpenDoc = (e: any) => {
      const doc = e.detail?.document;
      if (!doc) return;
      setContextContent({
        type: 'documents',
        title: doc.title || doc.name || 'Document',
        data: { documents: [doc] }
      });
    };
    const onOpenEmail = (e: any) => {
      const email = e.detail?.email;
      if (!email) return;
      setContextContent({
        type: 'emails',
        title: email.subject || 'Email',
        data: { emails: [email] }
      });
    };
    window.addEventListener('open-document-preview' as any, onOpenDoc);
    window.addEventListener('open-email-preview' as any, onOpenEmail);
    return () => window.removeEventListener('open-document-preview' as any, onOpenDoc);
  }, []);

  // Use refs to prevent duplicate processing
  const processingCodeRef = useRef<string | null>(null);
  const hasCheckedAuthRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const streamReaderRef = useRef<ReadableStreamDefaultReader | null>(null);

  // MCP service toggles (persisted)
  const defaultMcp = { notion: true, email: true, drive: true, calendar: true, websearch: true, memory: true, other: true };
  const [mcpServices, setMcpServices] = useState<Record<string, boolean>>(() => {
    const savedStr = localStorage.getItem('mcp-services');
    const saved = savedStr ? JSON.parse(savedStr) as Record<string, boolean> : {};
    // Merge with defaults to ensure all services (like calendar) are present
    return { ...defaultMcp, ...saved };
  });

  useEffect(() => {
    localStorage.setItem('mcp-services', JSON.stringify(mcpServices));
  }, [mcpServices]);

  // State to trigger sidebar refresh
  const [refreshSidebar, setRefreshSidebar] = useState(0);

  // Parse documents from message content and steps
  const parseDocumentsFromMessage = useCallback(async (message: Message) => {
    console.log('🔍 [DocumentPreview] Starting to parse documents for message:', message.id);
    console.log('📄 [DocumentPreview] Message content length:', message.content?.length || 0);
    console.log('🔧 [DocumentPreview] Message steps count:', message.steps?.length || 0);
    
    try {
      let documents: any[] = []
      
      // Parse documents from message content (URLs)
      if (message.content) {
        console.log('🔗 [DocumentPreview] Parsing URLs from content...');
        const urlDocuments = await documentService.parseDocumentsFromMessage(message.content)
        documents = [...documents, ...urlDocuments]
        console.log(`📎 [DocumentPreview] Found ${urlDocuments.length} documents from URLs`);
      }
      
      // Parse documents from agent steps
      if (message.steps) {
        console.log('🔧 [DocumentPreview] Parsing documents from steps...');
        const stepDocuments = documentService.parseDocumentsFromSteps(message.steps)
        documents = [...documents, ...stepDocuments]
        console.log(`📋 [DocumentPreview] Found ${stepDocuments.length} documents from steps`);
      }
      
      console.log(`📊 [DocumentPreview] Total documents before enrichment: ${documents.length}`);
      
      // Fetch full preview data for documents found in steps
      if (documents.length > 0) {
        console.log('💾 [DocumentPreview] Enriching documents with preview data...');
        const enrichedDocuments = await Promise.all(
          documents.map(async (doc, index) => {
            console.log(`🔍 [DocumentPreview] Enriching document ${index + 1}/${documents.length}: ${doc.name}`);
            if (doc.fileId && !doc.previewContent) {
              try {
                const fullPreview = await documentService.getDocumentPreview(doc.fileId)
                return fullPreview || doc
              } catch (error) {
                console.warn(`Failed to get preview for ${doc.name}:`, error);
                return doc;
              }
            }
            return doc
          })
        )
        
        console.log(`✅ [DocumentPreview] Enriched ${enrichedDocuments.length} documents`);
        setDocumentPreviews(prev => ({
          ...prev,
          [message.id]: enrichedDocuments
        }))
        console.log('💾 [DocumentPreview] Saved to state for message:', message.id);
      } else {
        console.log('🚫 [DocumentPreview] No documents found to display');
      }
    } catch (error) {
      console.error('❌ [DocumentPreview] Error parsing documents from message:', error)
    }
  }, [])

  // Parse documents when messages change
  useEffect(() => {
    const latestMessage = messages[messages.length - 1]
    if (latestMessage && latestMessage.role === 'assistant' && !documentPreviews[latestMessage.id]) {
      console.log('🔄 [DocumentPreview] New assistant message detected:', latestMessage.id, {
        hasContent: !!latestMessage.content,
        hasSteps: !!(latestMessage.steps && latestMessage.steps.length > 0),
        stepCount: latestMessage.steps?.length || 0
      })
      parseDocumentsFromMessage(latestMessage)
    } else if (latestMessage) {
      console.log('🔄 [DocumentPreview] Message skipped:', {
        messageId: latestMessage.id,
        role: latestMessage.role,
        alreadyProcessed: !!documentPreviews[latestMessage.id]
      });
    }
  }, [messages, documentPreviews, parseDocumentsFromMessage])

  // Show document previews in context panel when they're available  
  useEffect(() => {
    const latestMessage = messages[messages.length - 1];
    if (latestMessage && documentPreviews[latestMessage.id] && documentPreviews[latestMessage.id].length > 0) {
      const docs = documentPreviews[latestMessage.id];
      setContextContent({
        type: 'documents',
        data: { documents: docs },
        title: `Found ${docs.length} Document${docs.length > 1 ? 's' : ''}`
      });
    }
  }, [messages, documentPreviews]);

  // Also parse documents from streaming steps as they come in
  useEffect(() => {
    if (streamingSteps.length > 0) {
      console.log(`🌊 [DocumentPreview] Processing ${streamingSteps.length} streaming steps`);
      const stepDocuments = documentService.parseDocumentsFromSteps(streamingSteps)
      if (stepDocuments.length > 0) {
        console.log('🎯 [DocumentPreview] Found documents in streaming steps:', stepDocuments)
        const tempMessageId = 'streaming_preview'
        setDocumentPreviews(prev => ({
          ...prev,
          [tempMessageId]: stepDocuments
        }))
        console.log('💾 [DocumentPreview] Saved streaming documents to state');
      } else {
        console.log('🚫 [DocumentPreview] No documents found in streaming steps');
      }
    }
  }, [streamingSteps])

  // Update messages to include document previews
  const messagesWithDocuments = messages.map(message => ({
    ...message,
    documentPreviews: documentPreviews[message.id] || []
  }));

  // Listen for OAuth callback messages
  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      const expectedOrigin = new URL(BACKEND_URL).origin;
      if (event.origin !== expectedOrigin) {
        return;
      }
      
      const { type, code, error, state } = event.data;
      
      if (type === 'GOOGLE_OAUTH_CALLBACK' && code) {
        if (processingCodeRef.current) {
          return;
        }
        
        processingCodeRef.current = code;
        
        try {
          const success = await handleGoogleCallback(code, state);
          if (success) {
            console.log('Frontend: OAuth callback processed successfully');
          }
        } catch (error) {
          console.error('Frontend: Error processing OAuth callback:', error);
        } finally {
          processingCodeRef.current = null;
        }
      } else if (type === 'GOOGLE_OAUTH_ERROR') {
        console.error('Frontend: OAuth error:', error);
        alert('Authentication failed: ' + error);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Listen for token refresh failures  
  useEffect(() => {
    const handleTokenRefreshFailed = async (event: Event) => {
      const customEvent = event as CustomEvent;
      console.log('Token refresh failed event received:', customEvent.detail);
      const { accountsNeedingReauth, message } = customEvent.detail;
      
      // Show user-friendly message
      const shouldReauth = window.confirm(
        `Your Google account session has expired. ${message}\n\n` +
        `Would you like to re-authenticate now to continue using Google services?`
      );
      
      if (shouldReauth) {
        try {
          await initiateGoogleLogin(false);
        } catch (error) {
          console.error('Failed to initiate re-authentication:', error);
          alert('Failed to start re-authentication. Please try again.');
        }
      } else {
        // User declined re-authentication, they might have limited functionality
        console.log('User declined re-authentication');
      }
    };

    window.addEventListener('TOKEN_REFRESH_FAILED', handleTokenRefreshFailed);
    return () => window.removeEventListener('TOKEN_REFRESH_FAILED', handleTokenRefreshFailed);
  }, []);

  const initializeDriveService = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/token`, {
        credentials: 'include',
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (response.ok) {
        const { accessToken } = await response.json();
        if (accessToken) {
          await driveService.setAccessToken(accessToken);
        }
      }
    } catch (error) {
      console.error('Failed to initialize drive service:', error);
    }
  }, []);

  const checkAuthStatus = useCallback(async () => {
    try {
      setIsLoading(true);
      
      const response = await fetch(`${BACKEND_URL}/api/auth/user`, {
        credentials: 'include',
        headers: { 'Accept': 'application/json' },
      });
      
      if (response.ok) {
        const { user, connectedAccounts } = await response.json();
        setAuthState({
          isAuthenticated: true,
          user,
          connectedAccounts,
        });
        await initializeDriveService();
      } else {
        setAuthState({
          isAuthenticated: false,
          user: null,
          connectedAccounts: [],
        });
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setAuthState({
        isAuthenticated: false,
        user: null,
        connectedAccounts: [],
      });
    } finally {
      setIsLoading(false);
    }
  }, [initializeDriveService]);
  
  // Check authentication status on mount
  useEffect(() => {
    if (!hasCheckedAuthRef.current) {
      hasCheckedAuthRef.current = true;
      checkAuthStatus();
    }
  }, [checkAuthStatus]);

  // Auto-create a new chat session when user is authenticated and no active session
  useEffect(() => {
    const autoCreateChat = async () => {
      if (authState.isAuthenticated && !activeSessionId && !isLoading) {
        console.log('Auto-creating new chat session on page load...');
        try {
          // Ensure we're on chat view
          setCurrentView('chat');
          
          // Create new chat session
          const newSession = await chatService.createChatSession("New Chat");
          setActiveSessionId(newSession.id);
          
          // Clear any existing state for fresh start
          setMessages([]);
          setStreamingSteps([]);
          setStreamingContent("");
          setIsStreamingResponse(false);
          setMemorySteps([]);
          setShowMemorySteps(false);
          setDocumentPreviews({});
          
          console.log('Auto-created chat session:', newSession.id, 'and set view to chat');
          
          // Trigger sidebar refresh to show new session
          setRefreshSidebar(prev => prev + 1);
        } catch (error) {
          console.error('Failed to auto-create chat session:', error);
        }
      }
    };
    
    autoCreateChat();
  }, [authState.isAuthenticated, activeSessionId, isLoading]);

  const initiateGoogleLogin = useCallback(async (isLinkAccount = false) => {
    try {
      const authUrlEndpoint = isLinkAccount ? `${BACKEND_URL}/api/auth/google?isLinkAccount=true` : `${BACKEND_URL}/api/auth/google`;
      
      const response = await fetch(authUrlEndpoint, {
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to get auth URL: ${response.status} ${response.statusText} - ${errorText}`);
      }
      
      const data = await response.json();
      
      if (!data.url) {
        throw new Error('No auth URL returned from backend');
      }
      
      const { url } = data;

      const width = 500;
      const height = 600;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        url,
        'Google Sign In',
        `width=${width},height=${height},left=${left},top=${top}`
      );

      if (!popup) {
        alert('Popup blocked. Please allow popups for this site and try again.');
      }
    } catch (error) {
      console.error('Failed to initiate Google login:', error);
      
      let errorMessage = 'Failed to start login process. ';
      if (error instanceof Error) {
        errorMessage += error.message;
        if (error.message.includes('Failed to fetch')) {
          errorMessage = 'Cannot connect to backend server. Please check if the backend is running.';
        } else if (error.message.includes('CORS')) {
          errorMessage = 'CORS error. Please check backend CORS configuration.';
        }
      }
      
      alert(errorMessage);
    }
  }, []);

  const handleGoogleCallback = useCallback(async (code: string, state?: string) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/exchange`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ code, state })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        if ((response.status === 400 && errorData.error?.includes('already been used')) ||
            (response.status === 400 && errorData.code === 'DUPLICATE_CODE')) {
          await checkAuthStatus();
          return true; 
        }
        
        if (response.status === 500 && errorData.error === 'Failed to exchange authorization code') {
          await checkAuthStatus();
          return true;
        }
        
        throw new Error(errorData.error || `Failed to exchange code: ${response.status}`);
      }
      
      await checkAuthStatus();
      
      await initializeDriveService();
      
      return true;
    } catch (error) {
      console.error('Google auth callback failed:', error);
      
      if (error instanceof Error && 
          !error.message.includes('already been used') && 
          !error.message.includes('Failed to exchange authorization code')) {
        alert('Authentication failed. Please try again.');
      }
      
      return false;
    }
  }, [checkAuthStatus, initializeDriveService]);

  const logout = useCallback(async () => {
    try {
      await fetch(`${BACKEND_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Accept': 'application/json'
        }
      });
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setAuthState({
        isAuthenticated: false,
        user: null,
        connectedAccounts: [],
      });
      processingCodeRef.current = null;
    }
  }, []);

  // Load messages for active session
  useEffect(() => {
    if (activeSessionId) {
      // For now, we'll start with empty messages for each session
      // In a full implementation, you'd load messages from the backend
      setMessages([]);
      setStreamingSteps([]);
      setStreamingContent("");
      setIsStreamingResponse(false);
    }
  }, [activeSessionId]);

  const handleContinueFromMemory = useCallback(async (originalMessage: string) => {
    if (!activeSessionId) {
      console.error('No active session for continue operation');
      return;
    }

    // Store the original message for the continue API call
    const lastUserMessage = messages[messages.length - 1];
    const messageToContinue = lastUserMessage?.content || originalMessage;

    setIsStreamingResponse(true);
    setStreamingSteps([]);
    setStreamingContent("");
    setMemorySteps([]);

    try {
      const response = await fetch(`${BACKEND_URL}/api/chat/sessions/${activeSessionId}/continue`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          originalMessage: messageToContinue,
          mcpServices
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No reader available');

      let buffer = '';
      let assistantContent = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += new TextDecoder().decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const payload = JSON.parse(line.slice(6));
              
              if (typeof payload === 'string') {
                // Content chunk
                assistantContent += payload;
                setStreamingContent(prev => prev + payload);
              } else if (payload.type) {
                // Structured events
                switch (payload.type) {
                  case 'step':
                    setStreamingSteps(prev => [...prev, payload]);
                    break;
                  case 'memoryStep':
                    setMemorySteps(prev => [...prev, payload]);
                    break;
                  case 'complete':
                    // Add the final assistant message
                    const newMessage: Message = {
                      id: payload.messageId,
                      role: 'assistant',
                      content: assistantContent.trim(),
                      timestamp: new Date().toISOString(),
                      steps: streamingSteps
                    };
                    setMessages(prev => [...prev, newMessage]);
                    break;
                }
              }
            } catch (e) {
              console.error('Error parsing SSE data:', e);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error continuing from memory:', error);
    } finally {
      setIsStreamingResponse(false);
      setStreamingContent("");
      setStreamingSteps([]);
    }
  }, [activeSessionId, messages, mcpServices, streamingSteps]);

  const handleSendMessage = async (messageContent: string, attachments?: FileAttachment[]) => {
    if (!messageContent.trim() && (!attachments || attachments.length === 0)) return;
    
    // Auto-create a new chat session if there's no active session
    let currentSessionId = activeSessionId;
    if (!currentSessionId) {
      console.log('No active session, creating new chat session automatically...');
      try {
        const newSession = await chatService.createChatSession("New Chat");
        setActiveSessionId(newSession.id);
        currentSessionId = newSession.id;
        
        // Clear current state for new chat
        setMessages([]);
        setStreamingSteps([]);
        setStreamingContent("");
        setIsStreamingResponse(false);
        setMemorySteps([]);
        setShowMemorySteps(false);
        setDocumentPreviews({});
        
        console.log('Auto-created new chat session:', newSession.id);
        
        // Trigger sidebar refresh to show new session
        setRefreshSidebar(prev => prev + 1);
      } catch (error) {
        console.error('Failed to auto-create chat session:', error);
        alert('Failed to start new chat. Please try again.');
        return;
      }
    }

    setIsTyping(true);

    try {
      // Temporary user message
      const tempUserMessage: Message = {
        id: Date.now().toString(),
        role: 'user',
        content: messageContent,
        timestamp: new Date().toISOString(),
        attachments: attachments || undefined,
      }
      setMessages(prev => [...prev, tempUserMessage])

      // Initialize streaming states  
      const isMemoryEnabled = mcpServices.memory !== false;
      setStreamingSteps([{
        thought: isMemoryEnabled 
          ? "Searching through memory and gathering information..." 
          : "Processing your request...",
        action: isMemoryEnabled ? "memory_search" : "processing",
        action_input: { query: messageContent },
        observation: isMemoryEnabled 
          ? "Checking previous conversations and external sources..."
          : "Analyzing your request and determining the best approach..."
      }])
      setStreamingContent("")
      setIsStreamingResponse(true)
      setMemorySteps([])
      setShowMemorySteps(false)
      
      // Clean up any previous streaming document previews
      setDocumentPreviews(prev => {
        const { streaming_preview, ...rest } = prev
        return rest
      })

      // Open SSE stream with abort controller - use currentSessionId
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      const response = await fetch(`${BACKEND_URL}/api/chat/sessions/${currentSessionId}/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          message: messageContent, 
          attachments: attachments || undefined, 
          mcpServices, 
          timestamp: tempUserMessage.timestamp 
        }),
        signal: abortController.signal
      })
      
      if (!response.ok) throw new Error(`HTTP status ${response.status}`)
      const reader = response.body?.getReader()
      if (!reader) throw new Error('Stream not supported')
      streamReaderRef.current = reader;
      const decoder = new TextDecoder()
      let buffer = ''

      // Read SSE-style events
      while (true) {
        const { done, value } = await reader.read()
        if (done) {
          console.log('🏁 SSE stream ended (done=true)');
          break;
        }
        buffer += decoder.decode(value, { stream: true })
        const parts = buffer.split("\n\n")
        buffer = parts.pop()!
        for (const part of parts) {
          const lines = part.split("\n")
          let eventType = 'message'
          let dataLine = ''
          for (const line of lines) {
            if (line.startsWith('event: ')) eventType = line.slice(7).trim()
            else if (line.startsWith('data:')) dataLine += line.slice(5)
          }
          if (!dataLine) continue
          // Handle end event without JSON parse
          if (eventType === 'end') {
            setIsStreamingResponse(false)
            continue
          }
          // Parse JSON payload for other events
          let payload: any
          try {
            payload = JSON.parse(dataLine.trim())
          } catch (e) {
            console.error('Failed to parse SSE payload', e, dataLine)
            continue
          }
          console.log('📡 [App] SSE Event received:', eventType, payload?.emails ? `(${payload.emails.length} emails)` : '');
          switch (eventType) {
            case 'chunk':
              setStreamingContent(prev => prev + payload.text)
              break
            case 'step':
              // Intercept agentic send_email to pause for user approval
              if (payload.action === 'send_email') {
                const actionInput = payload.action_input || {};
                const to = actionInput.to || actionInput.recipient || actionInput.recipientEmail;
                const subject = actionInput.subject;
                const body = actionInput.body || actionInput.content;
                
                // Ensure 'to' is properly formatted
                const normalizedTo = Array.isArray(to) ? to.join(", ") : to;
                
                // Show email composer in side panel
                setContextContent({
                  type: 'email_composer',
                  data: {
                    to: normalizedTo,
                    subject,
                    content: body
                  },
                  title: 'Review & Send Email'
                });
                setIsStreamingResponse(false);
                return;
              }
              setStreamingSteps(prev => [...prev, payload])
              break
            case 'memoryStep':
              console.log('🧠 Frontend: Received memoryStep event:', payload)
              setMemorySteps(prev => [...prev, payload])
              setShowMemorySteps(true)
              
              // Memory steps are now only shown in chat, not in sidebar
              break
            case 'memoryResponse': {
              console.log('🧠 Frontend: Received memoryResponse event:', payload)
              
              // Show memory sources in canvas
              setContextContent({
                type: 'documents',
                data: {
                  sources: payload.sources,
                  memory_ids: payload.memory_ids,
                  completeness: payload.completeness,
                  isMemorySource: true
                },
                title: `Memory Sources (${Math.round(payload.completeness * 100)}% confidence)`
              });
              
              // Create a special memory response message
              const memoryMessage: Message = {
                id: Date.now().toString(),
                role: 'assistant',
                content: payload.content,
                timestamp: new Date().toISOString(),
                type: 'memory_response',
                memoryData: {
                  sources: payload.sources,
                  memory_ids: payload.memory_ids,
                  completeness: payload.completeness,
                  continue_data: payload.continue_data
                }
              }
              setMessages(prev => [...prev, memoryMessage])
              setIsStreamingResponse(false)
              break
            }
            case 'emailData': {
              // Store email data to be attached to the next assistant message
              (window as any).__pendingEmailData = payload.emails;
              // Show emails in context panel
              console.log('📧 [App] Setting context content for emails:', payload);
              console.log('📧 [App] Email count:', payload.emails?.length || 0);
              console.log('📧 [App] First email sample:', payload.emails?.[0]);
              console.log('📧 [App] About to call setContextContent with:', {
                type: 'emails',
                data: payload,
                title: `Found ${payload.emails?.length || 0} Emails`
              });
              // Don't override the composer while user is composing a reply
              if (!(isComposingEmail || contextContent.type === 'email_reply' || contextContent.type === 'email_composer')) {
                setContextContent({
                  type: 'emails',
                  data: payload,
                  title: `Found ${payload.emails?.length || 0} Emails`
                });
              }
              console.log('📧 [App] setContextContent called successfully');
              break;
            }
            case 'webSearchData': {
              // Store web search data to be attached to the next assistant message
              (window as any).__pendingWebSearchData = payload;
              // Show web search results in context panel
              setContextContent({
                type: 'web_search',
                data: payload,
                title: `Search Results: "${payload.query}"`
              });
              break;
            }
            case 'documentData': {
              // Show documents in context panel
              setContextContent({
                type: 'documents',
                data: payload,
                title: `Found ${payload.documents?.length || 0} Documents`
              });
              break;
            }
            case 'assistantMessage': {
              const msgWithDate = { ...payload, timestamp: new Date(payload.timestamp) }
              // Check if we have pending email data
              if ((window as any).__pendingEmailData) {
                msgWithDate.emails = (window as any).__pendingEmailData;
                (window as any).__pendingEmailData = null;
              }
              // Check if we have pending web search data
              if ((window as any).__pendingWebSearchData) {
                msgWithDate.webSearchData = (window as any).__pendingWebSearchData;
                (window as any).__pendingWebSearchData = null;
              }
              // Preserve reasoning steps from the backend
              if (payload.steps && payload.steps.length > 0) {
                msgWithDate.steps = payload.steps;
              }
              setMessages(prev => [...prev, msgWithDate])
              
              // Show content in context panel
              if (msgWithDate.emails && msgWithDate.emails.length > 0) {
                console.log('📧 [App] Setting context content for assistant message emails:', msgWithDate.emails);
                setContextContent({
                  type: 'emails',
                  data: { emails: msgWithDate.emails },
                  title: `Found ${msgWithDate.emails.length} Email${msgWithDate.emails.length > 1 ? 's' : ''}`
                });
              } else if (msgWithDate.webSearchData) {
                setContextContent({
                  type: 'web_search',
                  data: msgWithDate.webSearchData,
                  title: `Search Results: "${msgWithDate.webSearchData.query}"`
                });
              }
              
              // Save assistant message to memory system
              if (activeSessionId && msgWithDate.content && authState.user?.id) {
                // Don't await to avoid blocking the UI
                memoryService.saveMessage(authState.user.id.toString(), {
                  role: 'assistant',
                  model: 'agent',
                  text: msgWithDate.content,
                  conversationId: activeSessionId,
                  timestamp: msgWithDate.timestamp.getTime()
                }).then(() => {
                  console.log('Assistant message saved to memory system');
                }).catch(error => {
                  console.error('Error saving assistant message to memory:', error);
                });
              }
              
              break
            }
            case 'error': {
              const errMsg = typeof payload === 'string' ? payload : JSON.stringify(payload)
              setMessages(prev => [...prev, {
                id: Date.now().toString(),
                role: 'assistant',
                content: `Error: ${errMsg}`,
                timestamp: new Date().toISOString(),
              }])
              setIsStreamingResponse(false)
              break
            }
          }
        }
      }
    } catch (error) {
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        role: 'assistant',
        content: `Sorry, I encountered an error: ${error instanceof Error ? error.message : String(error)}`,
        timestamp: new Date().toISOString(),
      }])
    } finally {
      setIsTyping(false)
      setIsStreamingResponse(false)
      abortControllerRef.current = null;
      streamReaderRef.current = null;
      
      // Auto-name chat if this is the first message and title is still "New Chat"
      if (activeSessionId && messageContent.trim()) {
        try {
          // Check if session still has default title by getting current sessions
          const sessions = await chatService.getChatSessions();
          const currentSession = sessions.find(s => s.id === activeSessionId);
          
          if (currentSession && currentSession.title === 'New Chat') {
            // Generate a title from the first 50 characters of the message
            const autoTitle = messageContent.trim().length > 50 
              ? messageContent.trim().substring(0, 50) + '...'
              : messageContent.trim();
            
            // Update the session title
            await chatService.updateChatSessionTitle(activeSessionId, autoTitle);
            console.log(`Auto-named chat: "${autoTitle}"`);
          }
        } catch (error) {
          console.error('Error auto-naming chat:', error);
          // Don't throw - this is just a nice-to-have feature
        }
      }
      
      // Save user message to memory system
      if (activeSessionId && messageContent.trim() && authState.user?.id) {
        try {
          await memoryService.saveMessage(authState.user.id.toString(), {
            role: 'user',
            text: messageContent.trim(),
            conversationId: activeSessionId,
            timestamp: Date.now()
          });
          console.log('User message saved to memory system');
        } catch (error) {
          console.error('Error saving user message to memory:', error);
          // Don't throw - this is just a nice-to-have feature
        }
      }
    }
  };

  const handleSessionSelect = async (sessionId: string) => {
    try {
      setActiveSessionId(sessionId);
      
      // Load messages for the selected session
      const sessionMessages = await chatService.getMessages(sessionId);
      setMessages(sessionMessages);
      
      // Clear streaming state
      setStreamingSteps([]);
      setStreamingContent("");
      setIsStreamingResponse(false);
      setMemorySteps([]);
      setShowMemorySteps(false);
      
      // Clear document previews
      setDocumentPreviews({});
      
      console.log(`Loaded ${sessionMessages.length} messages for session ${sessionId}`);
    } catch (error) {
      console.error('Error loading session messages:', error);
      // Show error to user but still switch session
      setMessages([]);
    }
  };

  const handleNewChat = async () => {
    try {
      // Create a new chat session
      const newSession = await chatService.createChatSession();
      
      // Set as active session
      setActiveSessionId(newSession.id);
      
      // Clear current state for new chat
      setMessages([]);
      setStreamingSteps([]);
      setStreamingContent("");
      setIsStreamingResponse(false);
      setMemorySteps([]);
      setShowMemorySteps(false);
      setDocumentPreviews({});
      
      console.log('Created new chat session:', newSession.id);
      
      // Trigger sidebar refresh to show new session
      setRefreshSidebar(prev => prev + 1);
    } catch (error) {
      console.error('Error creating new chat session:', error);
      // Fallback: just clear state without creating session
      setActiveSessionId(null);
      setMessages([]);
      setStreamingSteps([]);
      setStreamingContent("");
      setIsStreamingResponse(false);
      setMemorySteps([]);
      setShowMemorySteps(false);
      setDocumentPreviews({});
    }
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleToggleReasoning = (messageId: string) => {
    setExpandedReasoning(prev => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  };

  const handleDocumentExpand = (document: any) => {
    // Document expansion removed - all documents shown inline in chat
    console.log('Document expand requested:', document.name);
  };

  const handleOpenSettings = () => {
    setShowMcpSettings(true);
  };

  const handleCloseMcpSettings = () => {
    setShowMcpSettings(false);
  };

  const handleUpdateMcpServices = (services: Record<string, boolean>) => {
    setMcpServices(services);
  };

  const handleOpenDocumentCanvas = (content: string, title: string, metadata?: any) => {
    setDocumentCanvas({
      isOpen: true,
      content,
      title,
      metadata
    });
  };

  const handleNavigateToSettings = () => {
    setCurrentView('account');
  };

  const handleNavigateToDocuments = () => {
    setCurrentView('documents');
  };

  const handleNavigateToTools = () => {
    setCurrentView('tools');
  };

  const handleNavigateToAccount = () => {
    setCurrentView('account');
  };

  const handleSearchChats = (query: string) => {
    // Implement chat search functionality
    console.log('Searching chats for:', query);
  };

  const handleDeleteSession = (sessionId: string) => {
    // If the deleted session was active, clear active session
    if (activeSessionId === sessionId) {
      setActiveSessionId(null);
      setMessages([]);
      setStreamingSteps([]);
      setStreamingContent("");
      setIsStreamingResponse(false);
      setMemorySteps([]);
      setShowMemorySteps(false);
      setDocumentPreviews({});
    }
  };

  const handleRenameSession = (sessionId: string, newTitle: string) => {
    // Just log for now - the sidebar handles the UI update
    console.log(`Session ${sessionId} renamed to: ${newTitle}`);
  };

  const handleNavigateToChat = async () => {
    setCurrentView('chat');
    
    // Always create a new chat when clicking "Chat" - this acts like "New Chat"
    await handleNewChat();
  };

  const handleRefreshToken = async () => {
    try {
      // This would call your token refresh endpoint
      await checkAuthStatus();
    } catch (error) {
      console.error('Failed to refresh token:', error);
    }
  };

  const handleCloseContext = () => {
    setContextContent({ type: 'none' });
  };

  const handleReplyEmail = async (email: any): Promise<void> => {
    console.log('Reply to email:', email);
    
    // Prepare reply data for the side canvas composer
    const replySubject = email.subject.startsWith('Re: ') 
      ? email.subject 
      : `Re: ${email.subject}`;
    
    // First, open the composer with a loading state
    setContextContent({
      type: 'email_reply',
      data: {
        to: email.from,
        subject: replySubject,
        content: 'Generating AI reply...',
        originalEmail: email,
        isLoading: true,
        emails: contextContent.type === 'emails' ? contextContent.data?.emails : undefined,
        currentIndex: contextContent.type === 'emails' && contextContent.data?.emails ? (contextContent.data.emails as any[]).findIndex((e: any) => e.id === email.id) : undefined
      },
      title: 'Reply to Email'
    });
 
    try {
      setIsComposingEmail(true);
      // Generate AI reply using the chat service
      if (!activeSessionId) {
        throw new Error('No active session');
      }
 
      // Create a prompt for generating the email reply
      const replyPrompt = `Please help me draft a professional email reply to this email. Follow these rules:\n- Include a proper greeting using the recipient's name inferred from the From field.
  * If the tone is informal or prior emails use first names, use: "Hi {FirstName},"
  * Otherwise use a formal salutation: "Dear {FullName},"
  * Do not include signatures or extraneous sign-offs unless clearly present in prior thread tone.
\n- Be concise, professional, and address the original content.
\n- Return only the email body content (no extra commentary, no quotes of the original email, no separators).
 
From: ${email.from}
Subject: ${email.subject}
Date: ${email.date || email.parsedDate}
 
Original Email:
${email.fullBody || email.snippet}
 
Please write a thoughtful, professional reply using the greeting rule above. Only provide the email body content.`;
 
      // Send the prompt to get AI reply
      const response = await fetch(`${BACKEND_URL}/api/chat/sessions/${activeSessionId}/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          message: replyPrompt,
          mcpServices: mcpServices,
          noPersist: true
        })
      });
 
      if (!response.ok) {
        throw new Error('Failed to generate reply');
      }
 
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let aiReplyContent = '';
      let isCollectingContent = false;
 
      if (reader) {
        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
 
            const chunk = decoder.decode(value);
            const lines = chunk.split('\n');
 
            for (const line of lines) {
              if (line.startsWith('event: assistantMessage')) {
                isCollectingContent = true;
                continue;
              }
              
              if (line.startsWith('data: ') && isCollectingContent) {
                try {
                  const data = JSON.parse(line.slice(6));
                  if (data.content) {
                    aiReplyContent = data.content; // complete content
                    setContextContent({
                      type: 'email_reply',
                      data: {
                        to: email.from,
                        subject: replySubject,
                        content: aiReplyContent,
                        originalEmail: email,
                        isLoading: false,
                        emails: contextContent.type === 'emails' ? contextContent.data?.emails : undefined,
                        currentIndex: contextContent.type === 'emails' && contextContent.data?.emails ? (contextContent.data.emails as any[]).findIndex((e: any) => e.id === email.id) : undefined
                      },
                      title: 'Reply to Email'
                    });
                  }
                } catch (e) {
                  // Ignore JSON parse errors
                }
              }
              
              if (line.startsWith('event: complete')) {
                isCollectingContent = false;
                break;
              }
            }
          }
        } finally {
          reader.releaseLock();
        }
      }
 
      // If no content was generated, show an error
      if (!aiReplyContent.trim()) {
        throw new Error('No reply content generated');
      }
 
    } catch (error) {
      console.error('Error generating email reply:', error);
      
      // Show error state and let user write manually
      const errorContent = `[Error generating AI reply. Please write your reply manually.]\n`;
 
      setContextContent({
        type: 'email_reply',
        data: {
          to: email.from,
          subject: replySubject,
          content: errorContent,
          originalEmail: email,
          isLoading: false,
          emails: contextContent.type === 'emails' ? contextContent.data?.emails : undefined,
          currentIndex: contextContent.type === 'emails' && contextContent.data?.emails ? (contextContent.data.emails as any[]).findIndex((e: any) => e.id === email.id) : undefined
        },
        title: 'Reply to Email'
      });
    } finally {
      setIsComposingEmail(false);
    }
  };
 
  const handleSendEmail = async (email: any, originalEmail?: any): Promise<void> => {
    if (!activeSessionId) {
      console.error('No active session for email sending');
      return;
    }
 
    try {
      console.log('📧 Sending approved email:', email);
      
      const response = await fetch(`${BACKEND_URL}/api/chat/sessions/${activeSessionId}/approve-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ emailData: email, originalEmail: originalEmail || (contextContent.type === 'email_reply' ? contextContent.data?.originalEmail : undefined) })
      });
 
      if (!response.ok) {
        throw new Error(`HTTP status ${response.status}`);
      }
 
      const result = await response.json();
       
      if (result.success) {
        console.log('✅ Email sent successfully:', result.message);
        // Mark as responded
        const orig = originalEmail || (contextContent.type === 'email_reply' ? contextContent.data?.originalEmail : undefined);
        if (orig?.id) {
          setRespondedEmailIds(prev => new Set(prev).add(orig.id));
          // Also update the visible emails in context so carousel reflects responded state
          if (contextContent.type === 'emails' && contextContent.data?.emails) {
            setContextContent({
              type: 'emails',
              data: { emails: contextContent.data.emails.map((e: any) => e.id === orig.id ? { ...e, isResponded: true } : e) },
              title: contextContent.title
            });
          } else if (contextContent.type === 'email_reply' && contextContent.data?.emails) {
            setContextContent({
              type: 'email_reply',
              data: { ...contextContent.data, emails: contextContent.data.emails.map((e: any) => e.id === orig.id ? { ...e, isResponded: true } : e) },
              title: contextContent.title
            });
          }
        }
        // Move to next email if available
        const list: any[] | undefined = contextContent.type === 'email_reply' ? contextContent.data?.emails : undefined;
        const idx: number = contextContent.type === 'email_reply' ? (contextContent.data?.currentIndex ?? -1) : -1;
        if (list && list.length > 0 && idx >= 0) {
          // Find next not-responded email
          let nextIdx = -1;
          for (let i = idx + 1; i < list.length; i++) {
            if (!respondedEmailIds.has(list[i].id)) { nextIdx = i; break; }
          }
          if (nextIdx === -1) {
            for (let i = 0; i < list.length; i++) {
              if (!respondedEmailIds.has(list[i].id)) { nextIdx = i; break; }
            }
          }
          if (nextIdx !== -1) {
            await handleReplyEmail(list[nextIdx]);
          } else {
            // No pending emails, show the email list again and keep panel open
            setContextContent({ type: 'emails', data: { emails: list }, title: `Emails (${list.length})` });
          }
        }
      } else {
        console.error('❌ Failed to send email:', result.error);
        alert(`Failed to send email: ${result.error}`);
      }
    } catch (error) {
      console.error('❌ Error sending email:', error);
      alert(`Error sending email: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleAIModifyEmail = async (instruction: string, currentEmail: any): Promise<any> => {
    try {
      if (!activeSessionId) {
        throw new Error('No active session');
      }

      // Create a prompt for AI email modification
      const modifyPrompt = `Please modify this email according to the following instructions: ${instruction}

Current email:
To: ${currentEmail.to}
Subject: ${currentEmail.subject}
Content: ${currentEmail.content}

Please respond with a JSON object containing the modified email in this exact format:
{
  "to": "modified_recipient@email.com",
  "subject": "Modified Subject", 
  "content": "Modified email content"
}

Only provide the JSON object, no additional text or explanations.`;

      // Send the prompt to get AI modification
      const response = await fetch(`${BACKEND_URL}/api/chat/sessions/${activeSessionId}/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          message: modifyPrompt,
          mcpServices: mcpServices,
          noPersist: true
        })
      });

      if (!response.ok) {
        throw new Error('Failed to modify email');
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let aiContent = '';

      if (reader) {
        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value);
            const lines = chunk.split('\n');

                          let currentEvent: string | null = null;
              for (const line of lines) {
                if (line.startsWith('event: ')) {
                  currentEvent = line.slice(7).trim();
                } else if (line.startsWith('data:') && currentEvent === 'assistantMessage') {
                  try {
                    const data = JSON.parse(line.slice(5).trim());
                    if (data.content) {
                      aiContent = data.content;
                    }
                  } catch (e) {
                    // Skip invalid JSON
                  }
                }
              }
          }
        } finally {
          reader.releaseLock();
        }
      }

      // Try to extract JSON from the AI response
      let modifiedEmail;
      try {
        // Look for JSON object in the response
        const jsonMatch = aiContent.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          modifiedEmail = JSON.parse(jsonMatch[0]);
        } else {
          throw new Error('No JSON found in AI response');
        }
      } catch (e) {
        // If JSON parsing fails, apply instruction as a simple modification
        console.warn('Failed to parse AI response as JSON, applying simple modification');
        modifiedEmail = {
          to: currentEmail.to,
          subject: currentEmail.subject,
          content: `${currentEmail.content}\n\n[Modified based on: ${instruction}]`
        };
      }

      return modifiedEmail;
    } catch (error) {
      console.error('Error modifying email:', error);
      // Return original email with error note
      return {
        ...currentEmail,
        content: `${currentEmail.content}\n\n[Error applying modification: ${instruction}]`
      };
    }
  };

  const refreshTokens = useCallback(async () => {
    try {
      console.log('Refreshing tokens...');
      const response = await fetch(`${BACKEND_URL}/api/auth/refresh-tokens`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Accept': 'application/json'
        }
      });

      const result = await response.json();
      
      if (response.ok) {
        console.log('Tokens refreshed successfully:', result);
        // Reload user data to get updated tokens
        await checkAuthStatus();
      } else {
        console.error('Token refresh failed:', result);
        if (result.action === 'REAUTH_REQUIRED') {
          alert('Some accounts need re-authentication. Please add them again.');
        }
      }
    } catch (error) {
      console.error('Error refreshing tokens:', error);
    }
  }, [checkAuthStatus]);

  const disconnectAccount = useCallback(async (accountId: number) => {
    try {
      console.log('Disconnecting account:', accountId);
      const response = await fetch(`${BACKEND_URL}/api/auth/accounts/${accountId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Accept': 'application/json'
        }
      });

      const result = await response.json();
      
      if (response.ok) {
        console.log('Account disconnected successfully');
        // Reload user data to get updated account list
        await checkAuthStatus();
      } else {
        console.error('Failed to disconnect account:', result);
        alert(result.error || 'Failed to disconnect account');
      }
    } catch (error) {
      console.error('Error disconnecting account:', error);
      alert('Failed to disconnect account');
    }
  }, [checkAuthStatus]);

  if (isLoading) {
    return (
      <div className="h-screen bg-slate-900 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-white mb-4"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // Show auth modal if not authenticated
  if (!authState.isAuthenticated) {
    return (
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthModal initiateGoogleLogin={initiateGoogleLogin} />
        </BrowserRouter>
      </QueryClientProvider>
    );
  }

  // Check if context panel should be visible
  const isContextPanelVisible = contextContent.type !== 'none';

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <div className="h-screen bg-slate-900 text-white flex overflow-hidden">
          {/* Sidebar - Always Visible with fixed positioning */}
          <div className={`${sidebarCollapsed ? 'w-16' : 'w-60'} transition-all duration-300 flex-shrink-0 h-full`}>
            <ChatSidebar 
              collapsed={sidebarCollapsed} 
              onToggle={toggleSidebar}
              activeSessionId={activeSessionId || undefined}
              onSessionSelect={(sessionId) => {
                handleSessionSelect(sessionId);
                setCurrentView('chat');
              }}
              onNewChat={handleNewChat}
              onNavigateToChat={handleNavigateToChat}
              onNavigateToSettings={handleNavigateToSettings}
              onNavigateToDocuments={handleNavigateToDocuments}
              onNavigateToTools={handleNavigateToTools}
              onNavigateToAccount={handleNavigateToAccount}
              onSearchChats={handleSearchChats}
              onDeleteSession={handleDeleteSession}
              onRenameSession={handleRenameSession}
              currentView={currentView}
              refreshTrigger={refreshSidebar}
            />
          </div>
          
          {/* Main Content + Resizable Side Panel */}
          <div className="flex-1 h-full min-w-0">
            <ResizablePanelGroup key={sidebarOpen ? 'open' : 'closed'} direction="horizontal" className="h-full">
              <ResizablePanel defaultSize={100 - sidebarPanelSize} minSize={30}>
                <div className="flex h-full">
                {currentView === 'documents' ? (
                  /* Documents Page - Full Width */
                  <div className="flex-1 h-full overflow-y-auto">
                    <DocumentsPage
                      mcpServices={mcpServices}
                      onNavigateToSettings={handleNavigateToSettings}
                    />
                  </div>
                ) : currentView === 'tools' ? (
                  /* Tools Page - Full Width */
                  <div className="flex-1 h-full overflow-y-auto">
                    <ToolsPage
                      mcpServices={mcpServices}
                      onUpdateMcpServices={handleUpdateMcpServices}
                    />
                  </div>
                ) : currentView === 'account' ? (
                  /* Account Page - Full Width */
                  <div className="flex-1 h-full overflow-y-auto">
                    <AccountPage
                      user={authState.user || undefined}
                      connectedAccounts={authState.connectedAccounts}
                      onLogout={logout}
                      onDisconnectAccount={disconnectAccount}
                      onInitiateGoogleLogin={initiateGoogleLogin}
                    />
                  </div>
                ) : (
                  /* Chat Interface - Fixed height layout */
                  <>
                    {/* Chat Container - Constrained height */}
                    <div className="flex-1 flex flex-col h-full min-w-0">
                      {/* In-chat account toggle bar */}
                      {authState.connectedAccounts.length > 0 && (
                        <div className="px-6 pt-4">
                          <div className="flex items-center gap-2 overflow-x-auto">
                            <span className="text-xs text-slate-400 mr-2">Accounts:</span>
                            {authState.connectedAccounts.map((acc) => {
                              const enabled = (mcpServices.email !== false);
                              return (
                                <button
                                  key={acc.id}
                                  onClick={() => {
                                    // Toggle email tool globally for simplicity; can be extended to per-account routing later
                                    handleUpdateMcpServices({ ...mcpServices, email: mcpServices.email === false ? true : false });
                                  }}
                                  className={`flex items-center gap-2 px-2 py-1 rounded-full border text-xs ${enabled ? 'border-blue-500/40 bg-blue-500/10 text-blue-200' : 'border-slate-600 bg-slate-800 text-slate-300'}`}
                                  title={`${enabled ? 'Enabled' : 'Disabled'}: ${acc.email}`}
                                >
                                  {acc.picture ? (
                                    <img src={acc.picture} alt={acc.name} className="w-4 h-4 rounded-full" />
                                  ) : (
                                    <div className="w-4 h-4 rounded-full bg-slate-600" />
                                  )}
                                  <span className="truncate max-w-[120px]">{acc.name}</span>
                                </button>
                              );
                            })}
                            <button
                              onClick={() => initiateGoogleLogin(true)}
                              className="ml-2 inline-flex items-center gap-1 px-2 py-1 rounded-full border border-green-500/40 bg-green-500/10 text-green-200 text-[11px] hover:bg-green-500/20"
                              title="Link another Google account"
                            >
                              + Add
                            </button>
                          </div>
                        </div>
                      )}
                      {/* Messages Panel - Fixed height with scrolling */}
                      <div className="flex-1 h-full min-h-0">
                        <ChatMessages
                          messages={messagesWithDocuments}
                          activeSessionId={activeSessionId || undefined}
                          isStreamingResponse={isStreamingResponse}
                          streamingSteps={streamingSteps}
                          streamingContent={streamingContent}
                          memorySteps={memorySteps}
                          showMemorySteps={showMemorySteps}
                          expandedReasoning={expandedReasoning}
                          onToggleReasoning={handleToggleReasoning}
                          onSendMessage={handleSendMessage}
                          onContinueFromMemory={handleContinueFromMemory}
                          onStartNewChatWithMessage={async (text: string) => {
                            // Start a brand new chat and send the edited text as the first message
                            await handleNewChat();
                            // slight delay to ensure activeSessionId is set
                            setTimeout(() => handleSendMessage(text), 0);
                          }}
                          onOpenDocumentCanvas={handleOpenDocumentCanvas}
                          mcpServices={mcpServices}
                          onUpdateMcpServices={handleUpdateMcpServices}
                        />
                      </div>
                      {/* Chat Input - Fixed at bottom */}
                      {messages.length > 0 && (
                        <div className="flex-shrink-0 p-6 bg-slate-900">
                          <ChatInput 
                            onSendMessage={handleSendMessage}
                            isTyping={isTyping || isStreamingResponse}
                            disabled={!activeSessionId} // Disable if no active session
                            onOpenSettings={handleOpenSettings}
                            mcpServices={mcpServices}
                            onUpdateMcpServices={handleUpdateMcpServices}
                            onStop={async () => {
                              try {
                                // Abort frontend stream
                                abortControllerRef.current?.abort();
                                streamReaderRef.current?.cancel().catch(() => {});
                                // Tell backend to cancel
                                if (activeSessionId) {
                                  await fetch(`${BACKEND_URL}/api/chat/sessions/${activeSessionId}/cancel`, {
                                    method: 'POST',
                                    credentials: 'include',
                                    headers: { 'Content-Type': 'application/json' }
                                  }).catch(() => {});
                                }
                              } finally {
                                setIsTyping(false);
                                setIsStreamingResponse(false);
                              }
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </>
                )}
                </div>
              </ResizablePanel>
              {/* Handle between main and side */}
              <ResizableHandle withHandle className="bg-slate-700/50" />
              <ResizablePanel defaultSize={sidebarOpen ? sidebarPanelSize : 0} minSize={0} collapsible onResize={(size) => setSidebarPanelSize(size)}>
                {/* Dynamic Context Panel */}
                {sidebarOpen ? (
                  <div className="h-full">
                    <SideTabPanel 
                      contextContent={contextContent}
                      onCloseContext={handleCloseContext}
                      onReplyEmail={handleReplyEmail}
                      onGenerateEmailReply={async (email: any) => {
                        // Reuse the generation logic to return only the content string
                        const replySubject = email.subject?.startsWith('Re: ') ? email.subject : `Re: ${email.subject}`;
                        const replyPrompt = `Please help me draft a professional email reply to this email. Follow these rules:\n- Include a proper greeting using the recipient's name inferred from the From field.\n  * If the tone is informal or prior emails use first names, use: \"Hi {FirstName},\"\n  * Otherwise use a formal salutation: \"Dear {FullName},\"\n  * Do not include signatures or extraneous sign-offs unless clearly present in prior thread tone.\n\n- Be concise, professional, and address the original content.\n\n- Return only the email body content (no extra commentary, no quotes of the original email, no separators).\n\nFrom: ${email.from}\nSubject: ${email.subject}\nDate: ${email.date || email.parsedDate}\n\nOriginal Email:\n${email.fullBody || email.snippet}`;

                        const response = await fetch(`${BACKEND_URL}/api/chat/sessions/${activeSessionId}/stream`, {
                          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
                          body: JSON.stringify({ message: replyPrompt, mcpServices, noPersist: true })
                        });
                        if (!response.ok) throw new Error('Failed to generate reply');
                        const reader = response.body?.getReader();
                        const decoder = new TextDecoder();
                        let aiReplyContent = '';
                        let currentEvent: string | null = null;
                        if (reader) {
                          try {
                            while (true) {
                              const { done, value } = await reader.read(); if (done) break;
                              const chunk = decoder.decode(value); const lines = chunk.split('\n');
                              for (const line of lines) {
                                if (line.startsWith('event: ')) currentEvent = line.slice(7).trim();
                                else if (line.startsWith('data:') && currentEvent === 'assistantMessage') {
                                  try { const data = JSON.parse(line.slice(5).trim()); if (data.content) aiReplyContent = data.content; } catch {}
                                }
                              }
                            }
                          } finally { reader.releaseLock(); }
                        }
                        if (!aiReplyContent.trim()) throw new Error('No reply content generated');
                        return aiReplyContent;
                      }}
                      onSendEmail={handleSendEmail}
                      onAIModifyEmail={handleAIModifyEmail}
                      mode="inline"
                    />
                  </div>
                ) : (
                  <div className="h-full bg-slate-900/40 border-l border-slate-800/60 flex items-center justify-center text-slate-600 text-xs">
                    {/* Sidebar closed - expand automatically when content arrives */}
                  </div>
                )}
              </ResizablePanel>
            </ResizablePanelGroup>
          </div>
        </div>

        {/* Document Canvas - Overlay */}
        <DocumentCanvas
          isOpen={documentCanvas.isOpen}
          onClose={() => setDocumentCanvas(prev => ({ ...prev, isOpen: false }))}
          content={documentCanvas.content}
          title={documentCanvas.title}
          metadata={documentCanvas.metadata}
        />

      </BrowserRouter>
    </QueryClientProvider>
  );
}
